import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { CheckboxTree } from "ui";

import { selectLayersByVersionId, useGetVersionConfigQuery } from "fond/api";
import { Store } from "fond/types";
import { generateCheckboxTreeItems } from "fond/utils/configurations";
import { BlockSpinner } from "fond/widgets";

import { Container } from "./LayerTree.styles";
import { HeaderLabel, LayerBoxHeader, SelectedText, StyledCaption } from "./NewExportModal/NewExportModal.styles";

interface LayerTreeProps {
  excludedKeys?: string[];
  onSelectionChange: (selected: string[]) => void;
  versionId: string | undefined;
  title?: string;
}

const LayerTree: React.FC<LayerTreeProps> = ({ excludedKeys = [], onSelectionChange, title = "", versionId }: LayerTreeProps) => {
  const { data: config, isLoading, isFetching } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });
  const layers = useSelector((state: Store) => (versionId ? selectLayersByVersionId(state, versionId) : null));
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const treeRef = useRef<CheckboxTree>(null);

  // Get all the existent layer keys from the layers
  const existentLayerKeys = useMemo(() => {
    if (!layers) return null;
    return layers.map<string>((layer) => layer.LayerKey).filter(Boolean);
  }, [layers]);

  const treeItems = useMemo(
    () => (config ? generateCheckboxTreeItems({ config, excludedKeys, enabledKeys: existentLayerKeys }) : []),
    [config, excludedKeys, existentLayerKeys]
  );

  const allIds = useMemo(() => {
    const ids: string[] = [];
    const traverse = (nodes: typeof treeItems) => {
      nodes.forEach((node) => {
        if (node.children) {
          traverse(node.children);
        } else if (!node.disabled) {
          ids.push(node.value);
        }
      });
    };
    traverse(treeItems);
    return ids;
  }, [treeItems]);

  const numTotalLayer = allIds.length;
  const numSelectedLayer = checked.length;

  // This effect is used to expand all nodes when the treeItems change
  useEffect(() => {
    treeRef.current?.onExpandAll();
  }, [treeItems]);

  const handleOnCheck = (nodeIds: string[]) => {
    setChecked(nodeIds);
    onSelectionChange(nodeIds);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allChecked = !event.target.checked;
    handleOnCheck(allChecked ? [] : allIds);
  };

  if (isLoading || isFetching) return <BlockSpinner containerProps={{ height: "100%" }} />;

  return (
    <Container>
      {versionId ? (
        <LayerBoxHeader>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              sx={{ height: 32 }}
              label={
                <Box>
                  <HeaderLabel>{title}</HeaderLabel>
                </Box>
              }
              control={
                <Checkbox
                  size="small"
                  data-testid="export-select-all-layers"
                  checked={numTotalLayer > 0 && numTotalLayer === numSelectedLayer}
                  indeterminate={numSelectedLayer > 0 && numTotalLayer !== numSelectedLayer}
                  onChange={handleSelectAll}
                />
              }
            />
            <StyledCaption>{`(${numTotalLayer} Layers)`}</StyledCaption>
          </Box>
          <SelectedText>{`${numSelectedLayer} Selected`}</SelectedText>
        </LayerBoxHeader>
      ) : (
        <LayerBoxHeader>
          <Box display="flex" alignItems="center">
            <Checkbox size="small" />
            <HeaderLabel sx={{ pr: 1 }}>Name</HeaderLabel>
            <StyledCaption>(Layers)</StyledCaption>
          </Box>
        </LayerBoxHeader>
      )}

      <CheckboxTree ref={treeRef} checked={checked} expanded={expanded} onCheck={handleOnCheck} onExpand={setExpanded} nodes={treeItems} />
    </Container>
  );
};
export default LayerTree;
